import React, { useContext, useEffect, useState } from "react";
import { Card, Col, Row, Spin, Table } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { EyeOutlined, HomeOutlined, EditOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppContext } from "../../../context/AppContextProvider";
import { getStockAdjustments } from "../../../network/network";
import { handleApiError } from "../../../utils/errorHandler";

const ViewStockAdjustment = () => {
  const { appConfig } = useContext(AppContext);
  const [stockAdjustment, setStockAdjustment] = useState({});
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  let navigate = useNavigate();
  let { adjustmentId } = useParams();

  useEffect(() => {
    setLoading(true);
    getStockAdjustments()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setStockAdjustment(
          response.data.data.stock_adjustments.stock_adjustments.find(
            (adjustment) => adjustment.id === Number(adjustmentId),
          ),
        );
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setLoading(false);
      });
  }, [adjustmentId, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "inventory/stock-adjustment",
      breadcrumbName: t("stock_adjustment"),
      icon: <EditOutlined />,
    },
    {
      path: "view_stock_adjustment",
      breadcrumbName: t("view_stock_adjustment"),
      icon: <EyeOutlined />,
    },
  ];

  const tableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <>{index + 1}</>;
      },
    },
    {
      title: t("image"),
      width: "85px",
      render: (record) => {
        return (
          <>
            <img
              alt={record.product.name_en}
              src={
                record.product.image
                  ? record.product.image
                  : "/images/image-not-found.png"
              }
              className="app-table-thumbnail"
            />
          </>
        );
      },
    },
    {
      title: t("product"),
      render: (record) => {
        return (
          <>
            {appConfig.direction === "ltr"
              ? record.product.name_en
              : record.product.name}
          </>
        );
      },
    },
    {
      title: t("quantity"),
      render: (record) => {
        return <>{record.quantity}</>;
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          title={t("view_stock_adjustment")}
          onBack={() => navigate("/inventory/stock-adjustment")}
          ghost={false}
          className="app-p-0"
        />
      </Card>
      <Spin spinning={loading}>
        <Card
          style={{
            direction: appConfig.direction
              ? appConfig.direction === "ltr"
                ? "ltr"
                : "rtl"
              : "ltr",
          }}
        >
          {stockAdjustment.id && (
            <>
              <Row gutter={[20, 20]}>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("date")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.date_time
                      ? moment(stockAdjustment.date_time).format(
                          "MMM. D, YYYY hh:mm a",
                        )
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("created_by")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.created_by
                      ? stockAdjustment.created_by.name
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("reference_no")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.reference_no
                      ? stockAdjustment.reference_no
                      : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("branch")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.branch ? stockAdjustment.branch.name : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("quantity")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.total_quantity ? (
                      <div
                        style={{
                          color:
                            Math.sign(stockAdjustment.total_quantity) === 1
                              ? "var(--color-success)"
                              : "var(--color-danger)",
                        }}
                      >
                        <span dir="ltr" style={{ unicodeBidi: "isolate" }}>
                          {stockAdjustment.total_quantity}
                        </span>
                      </div>
                    ) : (
                      "-"
                    )}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={12}
                  lg={12}
                  xl={8}
                  xxl={8}
                >
                  <div className="app-detail-label">{t("note")}</div>
                  <div className="app-detail-value">
                    {stockAdjustment.note ? stockAdjustment.note : "-"}
                  </div>
                </Col>
                <Col
                  className="gutter-row"
                  xs={24}
                  sm={24}
                  md={24}
                  lg={24}
                  xl={24}
                  xxl={24}
                >
                  <Table
                    columns={tableColumns}
                    dataSource={stockAdjustment.stocks}
                    rowKey="id"
                    pagination={false}
                  />
                </Col>
              </Row>
            </>
          )}
        </Card>
      </Spin>
    </>
  );
};

export default ViewStockAdjustment;
